import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { scaleLinear } from 'd3-scale';
import { TiEye } from 'react-icons/ti';

/*
Grantham score is a prediction of the effect of substitutions between amino acids 
based on chemical properties, including polarity and molecular volume, characterised 
into classes of increasing chemical dissimilarity: conservative (0-50), moderately 
conservative (51-100), moderately radical (101-150), or radical (≥151).
*/

import ProgressBar from 'react-bootstrap/ProgressBar';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Form from 'react-bootstrap/Form';
import FormCheck from 'react-bootstrap/FormCheck';
import Table from 'react-bootstrap/Table';

import Residue from '../../models/residue';
import ProteinSummaryFigure from './ProteinSummaryFigure';
import CancerTypes from '../../models/CancerTypes';

const RE_RESLABEL = /^(-?[0-9]+)([A-Z]?)$/;
const MAX_GRANTHAM_SCORE = 205;
const GRANTHAM_SCALE = scaleLinear()
    .domain([0, 50, 100, 150])
    .range(["green", "yellow", "orange", "red"])

const CANCER_TYPES = new CancerTypes()

function get_grantham_info(score) {

    /* conservative (0-50), moderately conservative (51-100), moderately radical (101-150), or radical (≥151) */
    let label = null;
    if (0 < score && score <= 50) {
        label = 'conservative';
    }
    else if (50 < score && score <= 100) {
        label = 'moderately conservative';
    }
    else if (100 < score && score <= 150) {
        label = 'moderately radical';
    }
    else if (score > 150) {
        label = 'radical';
    }
    const info = {
        score: score,
        label: label,
        percentage: (100 * score / MAX_GRANTHAM_SCORE),
        rgb: GRANTHAM_SCALE(score),
    };
    return info;
}

function NfeTable(props) {

    const [items, setItems] = useState([]);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    let funfamId = props.funfamId;
    let cathVersion = props.cathVersion;

    const highlightNfe = (nfe) => {
        // console.log("highlight nfe: ", nfe);
    }

    const unhighlightNfe = (nfe) => {
        // console.log("unhighlight nfe: ", nfe);
    };

    const toggleNfeSelection = (nfe) => {
        console.log("toggleNfeSelection", nfe.is_selected, nfe)
        const item = items.find(i => i.id === nfe.id);
        item.is_selected = item.is_selected ? false : true;
        setItems(items);
        const selectedResidues = items.filter(nfe => nfe.is_selected === true).map((nfe) => {
            const pdbid = nfe.rep_id.substring(0, 4);
            const chainid = nfe.rep_id.substring(4, 5);
            const seqno = null;
            let [_, reslabel, resinsert] = RE_RESLABEL.exec(nfe.pdb_res);
            if (resinsert === "") resinsert = null;
            return new Residue(pdbid, chainid, seqno, reslabel, resinsert)
        });
        if (props.selectedResidues) {
            // console.log("NfeTable.selectedResidues: ", selectedResidues)
            props.onChangeResidues(selectedResidues);
        }
    };

    useEffect(() => {
        let url = `/api/nfe/version/${cathVersion}`;
        if (funfamId) {
            url += `?funfam_id=${funfamId}`
        }
        console.log("Fetching NFEs: ", url);
        fetch(url)
            .then(res => res.json())
            .then(response => {
                console.log("NFE response", response)
                for (const nfe of response.items) {
                    nfe.is_selected = false;
                }
                console.log("setItems")
                setItems(response.items);
                console.log("setIsLoading")
                setIsLoading(false);
                console.log("setIsLoading.AFTER")
            })
            .catch(error => {
                console.log(error);
                setError(error);
            });
    }, [funfamId, cathVersion]);

    const renderTH = (colname, tooltip) => {
        return (
            <th scope="col">
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{tooltip}</Tooltip>}
                >
                    <div>{colname}</div>
                </OverlayTrigger>
            </th>
        )
    }

    if (error) {
        return <div>Error: {error.message}</div>;
    } else if (isLoading) {
        return <div>Loading...</div>;
    } else {
        return (
            <Table className="table-sm" hover>
                <thead>
                    <tr>
                        {renderTH("Mutation Type", "Tags associated with the mutations classified as putative Neo-Functional Events (NFEs)")}
                        {renderTH("Location", "Location of the mutation within the protein sequence")}
                        {renderTH("Mutation", "Information about the mutation identified as a putative Neo-Functional Event (NFE)")}
                        {renderTH("Disruption", "Grantham Score is a measure of the physical and chemical disruption caused by this mutation")}
                        {renderTH("Mapped Residue", "Residue within the representative PDB structure to which this mutation has been mapped (via the FunFam alignment)")}
                        {renderTH("View", "View the location of this mutation as mapped to the representative structure for this CATH Functional Family")}
                    </tr>
                </thead>
                <tbody>
                    {items.map(item => {
                        // console.log("nfe.item: ", item)
                        const [aa_from, aa_to] = item.vm_aa_change.split('/');
                        const grantham_info = get_grantham_info(item.grantham);
                        const selected_checkbox = (
                            <FormCheck type="switch" id={`selected-${item.id}`} checked={item.is_selected} label="" onChange={() => toggleNfeSelection(item)} />
                        );
                        const residue_annotations = [{
                            position: item.vm_seq_no,
                            label: `${item.vm_seq_no}:${aa_from}->${aa_to}`,
                        }];
                        const cancer_description = CANCER_TYPES.get_description(item.cancer_type)
                        return (
                            <tr key={item.id}
                                onMouseOver={() => highlightNfe(item)}
                                onMouseOut={() => unhighlightNfe(item)}
                            >
                                <td>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>{cancer_description}</Tooltip>}
                                    >
                                        <Badge size="sm" variant="outline-primary">{item.cancer_type}</Badge>
                                    </OverlayTrigger>
                                </td>
                                <td className="text-center">
                                    <ProteinSummaryFigure height={10} width={60} totalResidues={item.uniprot.length} residueAnnotations={residue_annotations} />
                                    <small>{item.uniprot.id}: {item.vm_seq_no}</small>
                                </td>
                                <td>{item.vm_aa_change}</td>
                                <td><ProgressBar now={grantham_info.percentage}
                                    title={item.grantham_score} variant="warning" /></td>
                                <td><small>{item.rep_id} <br /> ({item.pdb_res})</small></td>
                                <td><Form>{selected_checkbox}</Form></td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        );
    }
}

NfeTable.propTypes = {
    classes: PropTypes.object,
    funfamId: PropTypes.object,
}

export default NfeTable;